import axios from 'axios';
import {
  clearTokensIfCurrent,
  getAccessExpiry,
  getAccessToken,
  getRefreshToken,
  refreshTokens,
} from '@/lib/authTokens';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: BACKEND_URL,
});

// Endpoints that never need (or must not trigger) a token refresh.
const AUTH_PATHS = [
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/refresh',
  '/api/auth/logout',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
];
// Only these areas require a session; on public pages a dead session is simply forgotten.
const SIGNED_IN_AREAS = ['/professional', '/appraiser', '/admin'];
const REFRESH_AHEAD_MS = 60 * 1000;

function isAuthPath(url) {
  const path = (url || '').replace(/^https?:\/\/[^/]+/, '');
  return AUTH_PATHS.some((p) => path.startsWith(p));
}

function authHeaderOf(config) {
  const headers = config?.headers;
  if (!headers) return '';
  const value = typeof headers.get === 'function' ? headers.get('Authorization') : headers.Authorization;
  return value ? String(value) : '';
}

export function sendToLogin() {
  const path = window.location.pathname;
  if (!SIGNED_IN_AREAS.some((p) => path.startsWith(p))) return;
  try {
    localStorage.setItem('return_to', path + window.location.search);
    localStorage.removeItem('session_expires_at');
  } catch {
    /* storage unavailable */
  }
  window.location.href = '/login?expired=1';
}

api.interceptors.request.use(async (config) => {
  // An explicit Authorization header (rare) wins.
  if (authHeaderOf(config) || isAuthPath(config.url)) return config;

  let token = getAccessToken();
  if (token && getRefreshToken() && getAccessExpiry() - Date.now() < REFRESH_AHEAD_MS) {
    try {
      token = (await refreshTokens()).token || getAccessToken();
    } catch {
      token = getAccessToken(); // the response path decides what to do
    }
  }
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error;
    const tokenProblem = response?.status === 401 && response?.data?.code === 'token_invalid';
    // Any other 401 (for example "Current password is incorrect") is for the caller to show; never a sign-out.
    if (!tokenProblem || !config || isAuthPath(config.url)) return Promise.reject(error);

    const failedToken = authHeaderOf(config).replace(/^Bearer\s+/i, '');
    if (!config._retriedAfterRefresh && getRefreshToken()) {
      config._retriedAfterRefresh = true;
      try {
        const fresh = await refreshTokens();
        config.headers = { ...config.headers, Authorization: `Bearer ${fresh.token || getAccessToken()}` };
        return api(config);
      } catch (refreshError) {
        // Server restarting or offline: keep the tokens and let the caller retry later. Superseded: this session
        // was already ended or replaced in this browser, so there is nothing to clear and nowhere to send the user.
        if (refreshError?.code === 'refresh_unavailable' || refreshError?.superseded) {
          return Promise.reject(error);
        }
      }
    }
    if (clearTokensIfCurrent(failedToken)) sendToLogin();
    return Promise.reject(error);
  },
);

export default api;
